/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBuilding, 
  faUsers, 
  faSearch,
  faFilter,
  faBriefcase,
  faArrowLeft,
  faHome,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import '../styles/Companies.css';
import '../styles/CompaniesMobile.css';
import { formatEmployerName } from '../utils/nameFormatter';

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedSize, setSelectedSize] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    filterCompanies();
  }, [searchTerm, selectedIndustry, selectedSize, companies]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(buildApiUrl('/api/companies/list'));
      
      if (response.data && response.data.companies) {
        // Filter out NGOs - only show companies, not NGOs
        // NGOs should only appear on the /ngos page
        const companiesOnly = response.data.companies.filter(company => {
          const section = (company.section || '').toLowerCase();
          const isNGO = section === 'ngos' || section === 'ngo';
          
          // Also check if it's marked as an NGO in other ways
          if (isNGO) {
            console.log(`🚫 Filtering out NGO from Companies page: ${company.companyName}`);
            return false;
          }

          // Hide internal/demo company records from public companies list
          const name = (company.companyName || '').toLowerCase();
          const isDemoFlag =
            company.isDemoRecruiter === true ||
            company.is_demo_recruiter === true ||
            name === 'akshar jobs demo company' ||
            name === 'infotech maxpro' ||
            name === 'lukoa francis';
          if (isDemoFlag) {
            console.log(`🚫 Filtering out demo company from Companies page: ${company.companyName}`);
            return false;
          }

          return true;
        });
        
        console.log(`✅ Loaded ${companiesOnly.length} companies (filtered out ${response.data.companies.length - companiesOnly.length} NGOs)`);
        setCompanies(companiesOnly);
        setFilteredCompanies(companiesOnly);
      } else {
        console.warn('⚠️ No companies data in response');
        setCompanies([]);
        setFilteredCompanies([]);
      }
    } catch (error) {
      console.error('❌ Error fetching companies:', error);
      if (error.response) {
        console.error('Response error:', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('Request error: No response received');
      } else {
        console.error('Error message:', error.message);
      }
      setCompanies([]);
      setFilteredCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const filterCompanies = () => {
    let filtered = [...companies];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(company =>
        company.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.industry?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Industry filter
    if (selectedIndustry !== 'all') {
      filtered = filtered.filter(company => 
        company.industry === selectedIndustry
      );
    }

    // Company size filter
    if (selectedSize !== 'all') {
      filtered = filtered.filter(company => 
        company.companySize === selectedSize
      );
    }

    setFilteredCompanies(filtered);
  };

  const getUniqueIndustries = () => {
    const industries = companies.map(c => c.industry).filter(Boolean);
    return [...new Set(industries)].sort();
  };

  const getUniqueSizes = () => {
    const sizes = companies.map(c => c.companySize).filter(Boolean);
    return [...new Set(sizes)].sort();
  };

  const getTotalOpenPositions = () => {
    const total = companies.reduce((sum, company) => {
      const positions = company.openPositions || company.jobCount || 0;
      return sum + (Number(positions) || 0);
    }, 0);
    return total;
  };

  const handleCompanyClick = (company) => {
    // Disabled: Don't navigate on card click to prevent unwanted navigation
    // Users can click "View Open Positions" button or website link instead
    return;
  };

  const getCompanyInitials = (name) => {
    if (!name) return 'C';
    const words = name.split(' ');
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getCompanyLogoUrl = (company) => {
    const raw = company?.companyLogo || company?.logo || company?.logoUrl || company?.logo_url;
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw) || raw.startsWith('data:')) return raw;
    const normalized = raw.replace(/\\/g, '/');
    if (normalized.startsWith('/uploads/')) {
      return buildApiUrl(`/api/users/uploads/${normalized.replace(/^\/uploads\//, '')}`);
    }
    if (normalized.startsWith('uploads/')) {
      return buildApiUrl(`/api/users/uploads/${normalized.replace(/^uploads\//, '')}`);
    }
    return normalized;
  };

  return (
    <div className="companies-page">
      {/* Back to Homepage Button */}
      <button 
        className="back-to-home-btn" 
        onClick={() => navigate('/')}
        aria-label="Back to Homepage"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
        <span>Back to Home</span>
      </button>

      {/* Hero Section */}
      <div className="companies-hero">
        <div className="companies-hero-overlay"></div>
        <div className="companies-hero-content">
          <div className="hero-badge">
            <FontAwesomeIcon icon={faBuilding} className="badge-icon" />
            <span>TRUSTED PARTNERS</span>
          </div>
          <h1 className="companies-hero-title">
            Explore Top Employers <br />
            <span className="gradient-text">Hiring Talent Like You</span>
          </h1>
          <p className="companies-hero-subtitle">
            Connect with industry-leading organizations actively seeking exceptional professionals
          </p>
          <div className="companies-stats">
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faBuilding} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{companies.length}+</div>
                <div className="stat-label">Partner Employers</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faBriefcase} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{getUniqueIndustries().length}+</div>
                <div className="stat-label">Industries</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{getTotalOpenPositions()}</div>
                <div className="stat-label">Open Positions</div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="companies-filters-container">
        <div className="companies-filters">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search employers by name or industry..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <FontAwesomeIcon icon={faFilter} className="filter-icon" />
            <select
              value={selectedIndustry}
              onChange={(e) => setSelectedIndustry(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Industries</option>
              {/* Industry options hidden - showing only filtered companies */}
            </select>

            <select
              value={selectedSize}
              onChange={(e) => setSelectedSize(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Sizes</option>
              {/* Size options hidden - showing only filtered companies */}
            </select>
          </div>
        </div>

        <div className="results-count">
          Showing <strong>{filteredCompanies.length}</strong> {filteredCompanies.length === 1 ? 'employer' : 'employers'}
        </div>
      </div>

      {/* Companies Grid */}
      <div className="companies-container">
        {loading ? (
          <div className="companies-loading">
            <div className="loading-spinner"></div>
            <p>Loading employers...</p>
          </div>
        ) : filteredCompanies.length === 0 ? (
          <div className="no-companies">
            <FontAwesomeIcon icon={faBuilding} className="no-companies-icon" />
            <h3>No Employers Found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="companies-grid">
            {filteredCompanies.map((company, index) => (
              <div
                key={company._id || index}
                className="company-card"
                onClick={() => handleCompanyClick(company)}
              >
                <div className="company-card-header">
                  <div className="card-header-bg"></div>
                  <div className="company-logo-wrapper">
                    {company.showLogoOnWebsite !== false && getCompanyLogoUrl(company) ? (
                      <img
                        src={getCompanyLogoUrl(company)}
                        alt={formatEmployerName(company.companyName || 'Company')}
                        className="company-logo"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const placeholder = e.target.nextElementSibling;
                          if (placeholder) {
                            placeholder.style.display = 'flex';
                          }
                        }}
                        onLoad={(e) => {
                          const placeholder = e.target.nextElementSibling;
                          if (placeholder) {
                            placeholder.style.display = 'none';
                          }
                        }}
                      />
                    ) : null}
                    <div 
                      className="company-logo-placeholder"
                      style={{ 
                        display: (company.showLogoOnWebsite !== false && getCompanyLogoUrl(company)) ? 'none' : 'flex' 
                      }}
                    >
                      <span className="initials-text">{getCompanyInitials(formatEmployerName(company.companyName || 'Company'))}</span>
                    </div>
                  </div>
                  <div className="verified-badge verified-badge-circle" aria-label="Verified employer">
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </div>
                </div>

                <div className="company-card-body">
                  <h3 className="company-name">
                    {formatEmployerName(company.companyName || 'Company Name')}
                  </h3>
                  
                  <div className="company-detail">
                    <FontAwesomeIcon icon={faBriefcase} />
                    <span><strong>{company.openPositions || company.jobCount || 0}</strong> open positions</span>
                  </div>
                </div>

                {/* Footer intentionally left minimal for employer cards - no CTA button */}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Companies;

